/**
 * Mundalo - Portal City Guides index.
 *
 * Shows all 13 city cards. Each card displays an access state:
 *   • Owned  → "View Guide"
 *   • Not owned → "Get - $4.99" (Stripe checkout)
 *   • Free trial → Upgrade prompt
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, CheckCircle2, ArrowRight, Globe, Sparkles } from 'lucide-react';
import PortalLayout from './PortalLayout';
import SEO from '../../components/SEO';
import { useAuth } from '../../context/AuthContext';
import { extractApiError } from '../../lib/apiError';
import { notifyError } from '../../lib/notify';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PortalCityGuides = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const upsellMode = searchParams.get('upsell') === '1';
  const [cities, setCities] = useState([]);
  const [accessSlugs, setAccessSlugs] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  // iter 97 - Free trial gets READ-ONLY view of City Guides (Wave 2.1).
  // Previous behavior force-redirected trial users to /portal/upgrade, which
  // felt like a hard paywall and gave them no preview of the value. We now
  // let them see the catalog of cities and the Free Sample for each; the
  // Buy button below resolves to an upgrade prompt for trial users via the
  // existing `owns()` / upgrade flow.
  const isTrial = user?.role === 'free_trial';

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch(`${API}/api/cities`).then((r) => r.json()),
      fetch(`${API}/api/city-guides/access`, { credentials: 'include' }).then((r) => r.ok ? r.json() : { slugs: [], is_admin: false }),
    ]).then(([cityData, accessData]) => {
      if (cancelled) return;
      setCities(cityData.cities || []);
      setAccessSlugs(accessData.slugs || []);
      setIsAdmin(!!accessData.is_admin);
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const owns = (slug) => isAdmin || accessSlugs.includes(slug);

  const buy = async (slug) => {
    setBusy(slug);
    try {
      const r = await fetch(`${API}/api/city-guides/${slug}/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ success_path: `/portal/city-guides/${slug}`, cancel_path: '/portal/city-guides' }),
      });
      const d = await r.json().catch(() => ({}));
      if (d.already_owned) {
        navigate(`/portal/city-guides/${slug}`);
      } else if (d.url) {
        window.location.href = d.url;
      } else if (r.status === 402 && d?.detail?.error === 'tier_required') {
        navigate('/portal/upgrade?reason=cityGuides');
      } else {
        notifyError(extractApiError(d, 'Could not start checkout - please try again.'));
      }
    } catch {
      notifyError('Network error - please try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <PortalLayout activeTab="cityGuides">
      <SEO title="City Guides - Mundalo" description="Full Mundalo city guides for 13 Mexican cities." path="/portal/city-guides" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10" data-testid="portal-city-guides">
        <header className="mb-10">
          <p className="font-mono uppercase tracking-[0.18em] text-[10px] md:text-xs text-[#E8A830] mb-2 inline-flex items-center gap-1">
            <Globe className="w-3 h-3" /> Mundalo · City Guides
          </p>
          <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3">
            13 cities. <span className="not-italic">Honest answers.</span>
          </h1>
          <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 max-w-2xl">
            Budget tables, neighborhood breakdowns, healthcare costs, housing reality, and the move-in plan - for every city Mundalo covers.
            {user?.role === 'guided' && (
              <span className="block mt-2 text-sm text-[#1A5C6E]"><strong>Guided members</strong> get 3 guides free, auto-selected from your intake. Buy any additional guide for $4.99.</span>
            )}
          </p>
          {isTrial && (
            <div
              className="mt-5 inline-flex items-start gap-3 bg-[#2CA8A0]/12 border border-[#2CA8A0]/40 rounded-xl px-4 py-3 max-w-2xl"
              data-testid="city-guides-trial-banner"
            >
              <Sparkles className="w-5 h-5 text-[#2CA8A0] mt-0.5 shrink-0" />
              <p className="text-sm text-[#0A1F2E]">
                <strong>You're previewing City Guides.</strong> Trial members can browse all guide listings. Pick any paid plan to unlock the full city guides - neighborhoods, budget tables, healthcare costs, and the move-in plan.
              </p>
            </div>
          )}
          {upsellMode && (
            <div
              className="mt-5 inline-flex items-start gap-3 bg-[#E8A830]/15 border border-[#E8A830]/40 rounded-xl px-4 py-3 max-w-2xl"
              data-testid="upsell-banner"
            >
              <Sparkles className="w-5 h-5 text-[#E8A830] mt-0.5 shrink-0" />
              <p className="text-sm text-[#0A1F2E]">
                <strong>Pick your next city.</strong> Every additional Mundalo City Guide is $4.99 - tap any non-owned card below.
              </p>
            </div>
          )}
        </header>

        {loading ? (
          <p className="text-[#5A6B73]">Loading…</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {cities.map((c) => {
              const owned = owns(c.slug);
              const isBusy = busy === c.slug;
              return (
                <article
                  key={c.slug}
                  data-testid={`city-guide-card-${c.slug}`}
                  className={`bg-white border rounded-2xl overflow-hidden flex flex-col ${owned ? 'border-[#2CA8A0]' : 'border-[#EFE6CF]'}`}
                >
                  <div
                    className="aspect-[16/10] bg-cover bg-center"
                    style={{ backgroundImage: `url(${c.hero_image_url})` }}
                  />
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="font-display text-2xl text-[#0A1F2E]">{c.name}</h3>
                      {owned && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#2CA8A0]" data-testid={`owned-badge-${c.slug}`}>
                          <CheckCircle2 className="w-3 h-3" /> Owned
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#1A5C6E] uppercase tracking-widest mb-3">{c.state}</p>
                    <p className="text-sm text-[#0A1F2E]/75 mb-5 flex-1">{c.tagline || ''}</p>
                    {owned ? (
                      <Link
                        to={`/portal/city-guides/${c.slug}`}
                        data-testid={`view-guide-${c.slug}`}
                        className="inline-flex items-center justify-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-medium py-2.5 rounded-full transition"
                      >
                        View Guide <ArrowRight className="w-4 h-4" />
                      </Link>
                    ) : isTrial ? (
                      // iter 97 - trial users see the catalog but the buy CTA
                      // routes to /portal/upgrade with a contextual reason
                      // (purchase endpoint would 403 anyway).
                      <Link
                        to="/portal/upgrade?reason=cityGuides"
                        data-testid={`upgrade-guide-${c.slug}`}
                        className="inline-flex items-center justify-center gap-2 bg-[#2CA8A0] hover:bg-[#1A5C6E] text-white font-medium py-2.5 rounded-full transition"
                      >
                        <Lock className="w-3.5 h-3.5" /> Unlock with any paid plan
                      </Link>
                    ) : (
                      <button
                        onClick={() => buy(c.slug)}
                        disabled={isBusy}
                        data-testid={`buy-guide-${c.slug}`}
                        className="inline-flex items-center justify-center gap-2 bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-medium py-2.5 rounded-full transition disabled:opacity-60"
                      >
                        {isBusy ? 'Opening…' : <><Lock className="w-3.5 h-3.5" /> Get - $4.99</>}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalCityGuides;
